import { useEffect, useRef, useState } from 'react'

import { io, Socket } from 'socket.io-client'
import { useRoomViewStore } from 'store/roomView/roomView.store'

interface UsePatientStatusSocketProps {
  orgId?: string
  authToken: string
}

export const usePatientStatusSocket = ({
  orgId,
  authToken,
}: UsePatientStatusSocketProps) => {
  const [isConnected, setIsConnected] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isReconnectingRef = useRef(false)

  const { refetchAll } = useRoomViewStore()

  const createSocketConnection = () => {
    const baseUrl =
      process.env.REACT_APP_ISERVICES_BASE_URL || 'https://inventory.medgeo.com'
    const socketUrl = baseUrl.endsWith('/')
      ? `${baseUrl}patient-status`
      : `${baseUrl}/patient-status`

    const socket = io(socketUrl, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: true,
      auth: {
        token: authToken,
      },
    })

    return socket
  }

  const initializeSocket = () => {
    if (socketRef.current?.connected) {
      return
    }

    const socket = createSocketConnection()
    socketRef.current = socket

    socket.on('connect', () => {
      setIsConnected(true)
      isReconnectingRef.current = false

      if (orgId) {
        socket.emit('joinOrganization', { orgId })
      }
    })

    socket.on('disconnect', () => {
      setIsConnected(false)
    })

    socket.on('connect_error', () => {
      setIsConnected(false)
    })

    socket.on('reconnect', () => {
      setIsConnected(true)

      if (orgId) {
        socket.emit('joinOrganization', { orgId })
      }
    })

    socket.on('reconnect_attempt', () => {
      isReconnectingRef.current = true
    })

    socket.on('reconnect_failed', () => {
      setIsConnected(false)
      isReconnectingRef.current = false
    })

    socket.on('patientStatusChanged', () => {
      refetchAll()
    })

    socket.on('surgeryAdded', () => {
      refetchAll()
    })

    socket.on('appConfigChanged', () => {
      refetchAll()
    })
  }

  const handleVisibilityChange = () => {
    if (!document.hidden) {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      if (!socketRef.current?.connected && !isReconnectingRef.current) {
        if (socketRef.current) {
          socketRef.current.disconnect()
        }

        initializeSocket()
      }
    }
  }

  const handleOnlineStatus = () => {
    if (navigator.onLine) {
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!socketRef.current?.connected && !isReconnectingRef.current) {
          if (socketRef.current) {
            socketRef.current.disconnect()
          }

          initializeSocket()
        }
      }, 2000)
    } else {
      setIsConnected(false)
    }
  }

  useEffect(() => {
    initializeSocket()

    document.addEventListener('visibilitychange', handleVisibilityChange)

    window.addEventListener('online', handleOnlineStatus)
    window.addEventListener('offline', handleOnlineStatus)

    const handleFocus = () => {
      setTimeout(() => {
        if (!socketRef.current?.connected && !isReconnectingRef.current) {
          initializeSocket()
        }
      }, 1000)
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnlineStatus)
      window.removeEventListener('offline', handleOnlineStatus)
      window.removeEventListener('focus', handleFocus)

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }

      if (socketRef.current?.connected) {
        if (orgId) {
          socketRef.current.emit('leaveOrganization', { orgId })
        }
        socketRef.current.disconnect()
      }
    }
  }, [orgId, refetchAll])

  useEffect(() => {
    if (socketRef.current?.connected && orgId) {
      socketRef.current.emit('joinOrganization', { orgId })
    }
  }, [orgId])

  useEffect(() => {
    if (socketRef.current) {
      if (socketRef.current.connected) {
        if (orgId) {
          socketRef.current.emit('leaveOrganization', { orgId })
        }
        socketRef.current.disconnect()
      }

      initializeSocket()
    }
  }, [authToken, orgId])

  return {
    isConnected,
    socket: socketRef.current,
  }
}
